const KEY_PREFIX = 'focusedView.liveCall.';
const HEARTBEAT_MS = 15_000;
// A tab that crashes mid-call never clears its key, so a stale heartbeat stops counting. Generous because
// browsers throttle timers in background tabs to as little as once a minute.
const STALE_MS = 90_000;

const tabId = Math.random().toString(36).slice(2);
const ownKey = KEY_PREFIX + tabId;

function writeHeartbeat(): void {
	try {
		localStorage.setItem(ownKey, String(Date.now()));
	} catch {
		// Storage can be blocked; other tabs then just keep their normal ring.
	}
}

function clearHeartbeat(): void {
	try {
		localStorage.removeItem(ownKey);
	} catch {
		// See writeHeartbeat.
	}
}

export function isAnotherTabOnLiveCall(now = Date.now()): boolean {
	try {
		let busy = false;
		const staleKeys: string[] = [];
		for (let i = 0; i < localStorage.length; i++) {
			const key = localStorage.key(i);
			if (!key?.startsWith(KEY_PREFIX) || key === ownKey) continue;
			if (now - Number(localStorage.getItem(key)) < STALE_MS) busy = true;
			else staleKeys.push(key);
		}
		staleKeys.forEach(key => localStorage.removeItem(key));
		return busy;
	} catch {
		return false;
	}
}

/** Tells other tabs this one is on a live call until the returned function is called. */
export function announceLiveCall(): () => void {
	writeHeartbeat();
	const timer = setInterval(writeHeartbeat, HEARTBEAT_MS);
	window.addEventListener('pagehide', clearHeartbeat);
	window.addEventListener('pageshow', writeHeartbeat);
	return () => {
		clearInterval(timer);
		window.removeEventListener('pagehide', clearHeartbeat);
		window.removeEventListener('pageshow', writeHeartbeat);
		clearHeartbeat();
	};
}

/** Calls `onChange` whenever another tab starts or stops a live call, including when its heartbeat goes stale. */
export function watchOtherTabLiveCall(onChange: (busy: boolean) => void): () => void {
	let busy = isAnotherTabOnLiveCall();
	onChange(busy);
	const check = () => {
		const next = isAnotherTabOnLiveCall();
		if (next === busy) return;
		busy = next;
		onChange(busy);
	};
	const onStorage = (e: StorageEvent) => {
		if (e.key === null || e.key.startsWith(KEY_PREFIX)) check();
	};
	window.addEventListener('storage', onStorage);
	const timer = setInterval(check, HEARTBEAT_MS);
	return () => {
		window.removeEventListener('storage', onStorage);
		clearInterval(timer);
	};
}
